import { apiClient } from "features/shared/api/axiosInstance";
import type { ChangeGiftCardVisibilityRequest } from "common/models/api/models/ChangeGiftCardVisibilityRequest";
import type { CreateLocalGiftCardRequest } from "common/models/api/models/CreateLocalGiftCardRequest";
import type { LocalGiftcardKpiDto } from "common/models/api/models/LocalGiftcardKpiDto";
import type { PaginatedLocalGiftCardDto } from "common/models/api/models/PaginatedLocalGiftCardDto";
import type { PartnerDto } from "common/models/api/models/PartnerDto";
import type { PurchasedGiftCardDto } from "common/models/api/models/PurchasedGiftCardDto";
import type { UpdateLocalGiftCardActiveRequest } from "common/models/api/models/UpdateLocalGiftCardActiveRequest";
import type { UpdateLocalGiftCardRequest } from "common/models/api/models/UpdateLocalGiftCardRequest";

const MIME_EXTENSION_MAP: Record<string, string> = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-excel": "xls",
    "text/csv": "csv",
    "application/pdf": "pdf",
};

const CONTENT_DISPOSITION_FILENAME_REGEX = /filename\*=(?:UTF-8'')?([^;]+)|filename="?([^";]+)"?/i;

const parseFileNameFromContentDisposition = (contentDisposition: string): string | undefined => {
    const match = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_REGEX);
    const rawFileName = match?.[1] || match?.[2];
    return rawFileName ? decodeURIComponent(rawFileName.trim()) : undefined;
};

export const giftcardsApi = {
    uploadLogo: (file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        return apiClient.post<string>("/api/gift-cards/logo", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    getLocalGiftCards: (params?: { page?: number; pageSize?: number; query?: string; status?: boolean; type?: string; partner?: string }) =>
        apiClient.get<PaginatedLocalGiftCardDto>("/api/local-giftcards", { params }),

    getLocalGiftcardCategories: (params?: { query?: string }) => apiClient.get<string[]>("/api/local-giftcards/categories", { params }),

    getLocalGiftcardKpi: (params?: { type?: string }) => apiClient.get<LocalGiftcardKpiDto>("/api/local-giftcards/kpi", { params }),

    getPartnerGiftCardStrategies: (params?: { partner?: string }) =>
        apiClient.get<PartnerDto[]>("/api/gift-cards/partners-strategies", { params }),

    createLocalGiftcard: (body: CreateLocalGiftCardRequest) => apiClient.post("/api/local-giftcards", body),

    updateLocalGiftCard: (id: number, body: UpdateLocalGiftCardRequest) => apiClient.patch(`/api/local-giftcards/${id}`, body),

    deleteLocalGiftCard: (id: string) => apiClient.delete(`/api/local-giftcards/${id}`),

    updateLocalGiftCardActive: (id: string, body: UpdateLocalGiftCardActiveRequest) =>
        apiClient.patch(`/api/local-giftcards/${id}/active`, body),

    loadGiftCards: (formData: FormData) =>
        apiClient.post("/api/gift-cards/file", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),

    updateGiftCardFile: (giftCardId: number, formData: FormData) =>
        apiClient.post(`/api/gift-cards/file/${giftCardId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),

    changeGiftCardVisibility: (giftCardId: number, body: ChangeGiftCardVisibilityRequest) =>
        apiClient.put<PurchasedGiftCardDto>(`/api/gift-cards/visibility/${giftCardId}`, body),

    getPreviewVoucher: (params: { allyId: string; status: "COMPLETED" | "PENDING" }) =>
        apiClient.get<string>("/api/local-giftcards/preview-voucher", {
            params: { ...params, format: "html" },
        }),

    exportPurchasedGiftCards: async (params: {
        partner?: string;
        startDate?: string;
        endDate?: string;
        strategy?: "KTRONIX" | "DEFAULT" | "EXITO";
        status?: "PENDING" | "COMPLETED";
    }) => {
        const res = await apiClient.get<Blob>("/api/gift-cards/export", {
            params,
            responseType: "blob",
        });
        const contentType = String(res.headers["content-type"] || res.headers["Content-Type"] || "")
            .split(";")[0]
            .trim();
        const extension = MIME_EXTENSION_MAP[contentType] || "xlsx";

        const contentDisposition = String(res.headers["content-disposition"] || res.headers["Content-Disposition"] || "");
        const fileNameFromHeader = parseFileNameFromContentDisposition(contentDisposition);
        if (!fileNameFromHeader) {
            return { data: res.data, extension, fileName: undefined };
        }

        const lastDotIndex = fileNameFromHeader.lastIndexOf(".");
        const fileName = lastDotIndex > 0 ? fileNameFromHeader.slice(0, lastDotIndex) : fileNameFromHeader;
        const fileExtension = lastDotIndex > 0 ? fileNameFromHeader.slice(lastDotIndex + 1) : extension;
        return { data: res.data, extension: fileExtension, fileName };
    },
};
